import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { Plus, Heart } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { Button } from '../components/ui/Button';
import { WantedItemCard } from '../components/community/WantedItemCard';
import { WantedItemForm } from '../components/community/WantedItemForm';
import { useWantedItems, useCreateWantedItem, useUpdateWantedItem, useDeleteWantedItem } from '../hooks/useCommunity';
import { useToast } from '../components/ui/Toast';
import { useTranslation } from '../i18n';
import type { WantedItem, CreateWantedItemInput } from '@siliconvault/shared';
import { Route as rootRoute } from './__root';
import styles from './wanted.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/wanted',
  component: WantedPage,
});

function WantedPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data, isLoading } = useWantedItems();
  const createMutation = useCreateWantedItem();
  const updateMutation = useUpdateWantedItem();
  const deleteMutation = useDeleteWantedItem();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<WantedItem | null>(null);

  const items = data?.data ?? [];

  async function handleCreate(formData: CreateWantedItemInput) {
    try {
      await createMutation.mutateAsync(formData);
      toast(t('community.wantedItemCreated'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.wantedItemCreateFailed'), 'error');
    }
  }

  async function handleUpdate(formData: CreateWantedItemInput) {
    if (!editItem) return;
    try {
      await updateMutation.mutateAsync({ id: editItem.id, data: formData });
      setEditItem(null);
      toast(t('community.wantedItemUpdated'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.wantedItemUpdateFailed'), 'error');
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id);
      toast(t('community.wantedItemDeleted'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.wantedItemDeleteFailed'), 'error');
    }
  }

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('community.wantedList')}</h1>
          <Button size="small" onClick={() => setShowForm(true)}>
            <Plus size={14} strokeWidth={2} /> {t('community.addWantedItem')}
          </Button>
        </div>

        {isLoading ? (
          <div>{t('community.loadingWanted')}</div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            <Heart size={56} strokeWidth={1} className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>{t('community.noWantedItems')}</div>
            <div className={styles.emptyDesc}>{t('community.noWantedItemsDesc')}</div>
          </div>
        ) : (
          <div className={styles.grid}>
            {items.map((item) => (
              <WantedItemCard
                key={item.id}
                item={item}
                canEdit
                onEdit={(item) => setEditItem(item)}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        <WantedItemForm
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleCreate}
          isLoading={createMutation.isPending}
        />

        {editItem && (
          <WantedItemForm
            isOpen={!!editItem}
            onClose={() => setEditItem(null)}
            onSubmit={handleUpdate}
            editItem={editItem}
            isLoading={updateMutation.isPending}
          />
        )}
      </div>
    </Shell>
  );
}
